import { SimpleGrid } from '@chakra-ui/react';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { t } from '@lingui/core/macro';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { djs } from '@duke-hq/djs';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import { StudentEnrolmentHealth } from '@duke-hq/svc-api-db';

import buildLinkToEnrolments from '../../utils/buildLinkToEnrolments';
import useDashboardContext from '../../context/useDashboardContext';
import ErrorFallback from '../ErrorFallback';
import CardGroup from './shared/CardGroup';
import Section from './shared/Section';
import StatCard from './shared/StatCard';

type Props = {
  data?: RouterOutputs['dashboard']['getParticipantActivity'];
};

const healthFilters = [
  StudentEnrolmentHealth.BEHIND,
  StudentEnrolmentHealth.SLIPPING,
  StudentEnrolmentHealth.NOT_STARTED,
  StudentEnrolmentHealth.ON_TRACK,
];

function ParticipantActivityUI({ data }: Props) {
  const { thisWeek, overTwoWeeks, betweenTwoWeeksOneMonth, overOneMonth } = data ?? {};

  return (
    <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing="8px">
      <StatCard
        backgroundColour="bg.canvas"
        title={t`Last activity`}
        titleColour="fg.muted"
        description={t`+30 days`}
        descriptionColour="danger.emphasis"
        value={overOneMonth}
        valueColour="fg.emphasis"
        to={buildLinkToEnrolments({
          lastActivityEnd: djs.utc().subtract(30, 'days'),
          health: healthFilters,
        })}
      />
      <StatCard
        backgroundColour="bg.canvas"
        title={t`Last activity`}
        titleColour="fg.muted"
        description={t`15-30 days`}
        descriptionColour="warning.emphasis"
        value={betweenTwoWeeksOneMonth}
        valueColour="fg.emphasis"
        to={buildLinkToEnrolments({
          lastActivityStart: djs.utc().subtract(30, 'days'),
          lastActivityEnd: djs.utc().subtract(15, 'days'),
          health: healthFilters,
        })}
      />
      <StatCard
        backgroundColour="bg.canvas"
        title={t`Last activity`}
        titleColour="fg.muted"
        description={t`8-14 days`}
        descriptionColour="yellow.500"
        value={overTwoWeeks}
        valueColour="fg.emphasis"
        to={buildLinkToEnrolments({
          lastActivityStart: djs.utc().subtract(14, 'days'),
          lastActivityEnd: djs.utc().subtract(8, 'days'),
          health: healthFilters,
        })}
      />
      <StatCard
        backgroundColour="bg.canvas"
        title={t`Last activity`}
        titleColour="fg.muted"
        description={t`This week`}
        descriptionColour="success"
        value={thisWeek}
        valueColour="fg.emphasis"
        to={buildLinkToEnrolments({
          lastActivityStart: djs.utc().subtract(7, 'days'),
          lastActivityEnd: djs.utc(),
          health: healthFilters,
        })}
      />
    </SimpleGrid>
  );
}

function ParticipantActivityWithData() {
  const { filters } = useFiltersContext();
  const { data } = trpcTenant.dashboard.getParticipantActivity.useQuery(
    { filters },
    {
      suspense: true,
      trpc: {
        context: {
          skipBatch: true,
        },
      },
    },
  );

  if (!data || Object.keys(data).length === 0) {
    return <ErrorFallback />;
  }

  return <ParticipantActivityUI data={data} />;
}

export default function ParticipantActivity() {
  const { openPanel } = useDashboardContext();

  return (
    <Section
      title={t`Participant Activity`}
      tooltipText={t`Days since participant engaged with any course content`}
    >
      <CardGroup cardType="muted" title={t`Engagement`} openPanel={() => openPanel('Engagement')}>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Suspense fallback={<ParticipantActivityUI />}>
            <ParticipantActivityWithData />
          </Suspense>
        </ErrorBoundary>
      </CardGroup>
    </Section>
  );
}
